import { ATTR_ABBR, type AttrKey, RATING_MAX, tierById } from '#shared/core/ratings';
import { attrOfStat, type LeaderboardKind } from '#shared/domain/stats/leaderboards';
import type { RatingCard, RatingDelta } from '#shared/schemas/rating';
import { fill } from '@/copy';
import { formatDelta } from '@/lib/rating';
import { copy } from './copy';

/** Display helpers for the stats screens. Pure, so they are easy to test. */

/** How a leaderboard value reads: ovr 71, ppg 8.4, fg_pct 46%, wins 12. */
export function formatStat(kind: LeaderboardKind, v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return copy.player.notTracked;
  switch (kind) {
    case 'avg':
      return v.toFixed(1);
    case 'pct':
      return `${Math.round(v * 100)}%`;
    default:
      return String(Math.round(v));
  }
}

/** The label of a leaderboard stat id, attribute boards included. */
export function statLabel(id: string): string {
  const table = copy.stat as Record<string, string>;
  return table[id] ?? id.toUpperCase();
}

export function statHint(id: string): string {
  if (attrOfStat(id)) return copy.statHint.attr;
  const table = copy.statHint as Record<string, string>;
  return table[id] ?? '';
}

/** "just now", "5m ago", "3h ago", "yesterday", "4d ago", then a date. */
export function relTime(at: number, now: number): string {
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 60) return copy.feed.justNow;
  const m = Math.floor(s / 60);
  if (m < 60) return fill(copy.feed.minutes, { n: m });
  const h = Math.floor(m / 60);
  if (h < 24) return fill(copy.feed.hours, { n: h });
  const d = Math.floor(h / 24);
  if (d === 1) return copy.feed.yesterday;
  if (d < 7) return fill(copy.feed.days, { n: d });
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(new Date(at));
}

export function shortDate(at: number): string {
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' }).format(new Date(at));
}

/** The line under a card: what state it is in and what to do about it. null for a rated card. */
export function stateLine(
  card: Pick<RatingCard, 'state' | 'ratedGames' | 'lastEvidenceAt' | 'asOf'>,
  now: number,
): string | null {
  switch (card.state) {
    case 'unrated':
      return copy.state.unrated;
    case 'provisional': {
      const left = Math.max(0, 5 - card.ratedGames);
      return left > 0 ? fill(copy.state.provisional, { n: left }) : copy.state.provisionalDone;
    }
    case 'rusty': {
      const days = card.lastEvidenceAt === null ? null : Math.floor((now - card.lastEvidenceAt) / 86_400_000);
      return days === null ? null : days <= 0 ? copy.state.rustyToday : fill(copy.state.rusty, { n: days });
    }
    default:
      return null;
  }
}

/** Short delta lines for a recap: the tier move first, then OVR, then the biggest three attributes. */
export function deltaLines(d: RatingDelta): { tier: string | null; ovr: string; attrs: string[] } {
  const tier =
    d.tier.crossed && d.tier.after
      ? fill(d.tier.crossed === 'up' ? copy.delta.promoted : copy.delta.demoted, { tier: tierById(d.tier.after).name })
      : null;
  let ovr: string;
  if (d.ovr.after === null) ovr = copy.delta.unrated;
  else if (d.ovr.delta > 0) ovr = fill(copy.delta.ovrUp, { n: d.ovr.after });
  else if (d.ovr.delta < 0) ovr = fill(copy.delta.ovrDown, { n: d.ovr.after });
  else ovr = fill(copy.delta.ovrSame, { n: d.ovr.after });
  const attrs = d.attrs.slice(0, 3).map((a) => {
    const table = (a.delta > 0 ? copy.delta.up : copy.delta.down) as Record<AttrKey, string>;
    return fill(table[a.key], { n: Math.abs(a.delta) });
  });
  return { tier, ovr, attrs };
}

/** "+2" with a real minus, for delta pills. */
export const signed = formatDelta;

export const attrShort = (k: AttrKey): string => ATTR_ABBR[k];

/** Fill of an attribute bar from 0 to 1. */
export const barFraction = (v: number | null): number => (v === null ? 0 : Math.min(1, Math.max(0, v / RATING_MAX)));
